import { LitElement, html, css, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import * as echarts from 'echarts/dist/echarts'

import { PolarPlotResponse, WeatherDetails } from '../types/polar-schema-types'
import {
    chartColors,
    defaultZoneVisibility,
    ZoneVisibility,
} from '../types/polar-chart-types'
import './number-stepper'
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons'
import {
    getArrowSVG,
    getBarChartZones,
    BarChartZones,
    ZoneTypes,
} from '../utils/cartesian-utils'
import { polarPlotCartesianStyles } from '../style/polar-chart-styles'


@customElement('polar-plot-cartesian-chart')
export class PolarPlotCartesianChart extends LitElement {
    @property({ type: Array }) polarPlotData:
        | PolarPlotResponse
        | undefined
        | null = null

    @property({ type: Object }) originalVesselDetails:
        | WeatherDetails['weather']
        | null = null

    @property({ type: Number }) headingInputValue = 0;
    @property({ type: Number }) speedInputValue = 0;
    @property({ type: Number }) currentDegreeInputValue = 0
    @property({ type: Number }) currentSpeedInputValue = 0
    @property({ type: Array }) private transformedBarData: BarChartZones = []
    @property({ attribute: false }) isEdited = false;
    @property({ attribute: false }) zoneVisibility: ZoneVisibility = {
        ...defaultZoneVisibility,
    };

    private chart: any
    private _degreeIncrement: number = 5

    private _chartRangeLength: number = 19
    // track the last computed window so currentRange() can return the same
    // _filteredData reference when heading/speed move within the same
    // window, letting updated() tell "window shifted" apart from "marker moved"
    private _lastRangeStart: number | null = null
    private _lastRangeEnd: number | null = null
    private _lastRangeSource: BarChartZones | null = null

    @state() private _filteredData: BarChartZones = []

    static styles = polarPlotCartesianStyles

    constructor() {
        super()
    }

    protected willUpdate(changedProperties: PropertyValues) {
        if (changedProperties.has('polarPlotData')) {
            this.currentDegreeInputValue = this.polarPlotData?.course_over_ground ?? 0;
            this.currentSpeedInputValue = this.polarPlotData?.speed_over_ground ?? 0;
        }
        if (changedProperties.has('polarPlotData') || changedProperties.has('zoneVisibility')) {
            this.transformedBarData = this.getTransformedBarData()
        }
        if (
            changedProperties.has('headingInputValue') ||
            changedProperties.has('speedInputValue') ||
            changedProperties.has('transformedBarData') ||
            changedProperties.has('zoneVisibility')
        ) {
            this._filteredData = this.currentRange()
        }
    }

    roundToIncrement = (value: number, increment: number): number => {
        return Math.round(value / increment) * increment;
    }

    firstUpdated() {
        this.initializeChart()

        // event listeners for showing and hiding tooltip
        this.chart.on(
            'mouseover',
            (params: {
                componentType: string
                seriesName: string
                seriesIndex: number
                dataIndex: number
            }) => {
                if (
                    params.componentType === 'series' &&
                    params.seriesName === 'current-heading-speed'
                ) {
                    this.chart.dispatchAction({
                        type: 'showTip',
                        seriesIndex: params.seriesIndex,
                        dataIndex: params.dataIndex,
                    })
                }
            },
        )

        this.chart.on('mouseout', () => {
            this.chart.dispatchAction({ type: 'hideTip' })
        })
    }

    initializeChart() {
        const container = this.renderRoot.querySelector('#chart') as HTMLElement

        this.chart = echarts.init(container)

        const options = this.getChartOptions()
        this.chart.setOption(options)

        window.addEventListener('resize', () => {
            if (this.chart != null && this.chart != undefined) {
                this.chart.resize()
            }
        })
    }

    updated(changedProperties: PropertyValues) {
        if (!this.chart) return

        // transformedBarData/_filteredData only change when polarPlotData or
        // zoneVisibility actually change the underlying zones (currentRange()
        // memoizes the visible window so a heading/speed nudge that stays
        // inside the same window doesn't touch _filteredData at all)
        const needsFullRebuild =
            changedProperties.has('transformedBarData') ||
            changedProperties.has('_filteredData')

        if (needsFullRebuild) {
            const options = this.getChartOptions()

            // to prevent lingering graphics from previous renders and explicitly replacing them
            this.chart.setOption(
                { series: [], graphic: [] },
                { replaceMerge: ['series', 'graphic'] },
            )

            // set the updated options with new series and graphics
            this.chart.setOption(options, {
                replaceMerge: ['series', 'graphic'],
            })
        } else if (
            changedProperties.has('headingInputValue') ||
            changedProperties.has('speedInputValue') ||
            changedProperties.has('isEdited')
        ) {
            this.updateVesselMarkers()
        }
    }

    private currentRange() {
        if (!this.transformedBarData) return []

        const currentHeading = this.roundToIncrement(
            this.headingInputValue,
            this._degreeIncrement
        );

        const middleRange = Math.floor(this._chartRangeLength / 2)
        const currentHeadingIndex = this.transformedBarData.findIndex(
            (d) => d.degree === currentHeading,
        )
        // calculate the ideal start and end indices
        let start = Math.max(0, currentHeadingIndex - middleRange)
        let end = start + this._chartRangeLength

        // adjusting start if end goes beyond array bounds
        if (end > this.transformedBarData.length) {
            end = this.transformedBarData.length
            start = Math.max(0, end - this._chartRangeLength)
        }

        // same window as last time (heading/speed moved but didn't cross a
        // window boundary) - return the same reference so Lit sees no change
        // and updated() doesn't rebuild the zone bars for nothing
        if (
            start === this._lastRangeStart &&
            end === this._lastRangeEnd &&
            this.transformedBarData === this._lastRangeSource
        ) {
            return this._filteredData
        }

        this._lastRangeStart = start
        this._lastRangeEnd = end
        this._lastRangeSource = this.transformedBarData
        return this.transformedBarData.slice(start, end)
    }

    private getTransformedBarData() {
        const {
            roll_risk_polygons,
            maximum_speed_polygon,
            minimum_speed_polygon,
            parametric_roll_risk_polygons,
            transverse_acceleration_risk_polygons,
            critical_roll_model
        } = this.polarPlotData || {}

        return getBarChartZones({
            heavyRollingPolygons: roll_risk_polygons,
            parametricRollRiskPolygons: parametric_roll_risk_polygons,
            transverseAccelerationRiskPolygons: transverse_acceleration_risk_polygons,
            minSpeedPolygon: minimum_speed_polygon || [],
            maxSpeedPolygon: maximum_speed_polygon || [],
            criticalRollModel: critical_roll_model,
            zoneVisibility: this.zoneVisibility,
        })
    }

    private getBarDataForRange(range: number[], index: number) {
        const stackedData = Array(this.transformedBarData.length).fill(0) // initialize all with zeros

        // fill in the specific bar for the current degree, using the range's start and end points
        stackedData[index] = range[1] - range[0]

        return stackedData
    }

    // Builds the two series that represent the adjustable "new heading/speed"
    // marker + cross-hair. Shared by the full chart rebuild and the
    // lightweight marker-only update so both stay in sync.
    private buildVesselMarkerSeries() {
        const newHeading = this.headingInputValue
            ? this.roundToIncrement(this.headingInputValue, this._degreeIncrement)
            : 0;
        const newIndex = this._filteredData.findIndex(
            (d: { degree: number }) => d.degree === newHeading
        );

        const newIconSVG =
            'data:image/svg+xml,' +
            encodeURIComponent(SvgIcons.newChartCircle.strings[0])

        const shouldDisplayNewHeadingSpeed =
        (this.headingInputValue !== this.currentDegreeInputValue || this.speedInputValue !== this.currentSpeedInputValue) && this.isEdited;

        const newHeadingSpeedSeries = {
            id: 'new-heading-speed-series',
            name: 'new-heading-speed',
            type: 'custom', // Use scatter to place a mark point at specific coordinates
            data: shouldDisplayNewHeadingSpeed ? [[newIndex, this.speedInputValue]] : [], // Coordinates where the circle should appear
            triggerLineEvent: true,
            renderItem: (
                _: any,
                api: {
                    coord: (arg0: any[]) => any[];
                    value: (arg0: number) => any;
                }
            ) => {
                const x = api.coord([api.value(0), api.value(1)])[0];
                const y = api.coord([api.value(0), api.value(1)])[1];
                return {
                    type: 'image',
                    style: {
                        image: newIconSVG,
                        height: 100,
                        width: 100,
                        x: x - 100 / 2,
                        y: y - 92 / 2,
                    },
                };
            },
            z: 80,
        }

        const newLinesSeries = {
            id: 'new-lines-series',
            name: 'Cross Lines',
            type: 'line',
            silent: true,
            data: [],
            markLine: shouldDisplayNewHeadingSpeed ? {
                symbol: 'none',
                label: { show: false },
                data: [
                    {
                        yAxis: this.speedInputValue,
                        lineStyle: {
                            color: chartColors.greyDark,
                            width: 1,
                            type: 'dashed',
                        },
                    },
                    // Vertical line
                    {
                        xAxis: newIndex,
                        lineStyle: {
                            color: chartColors.greyDark,
                            width: 1,
                            type: 'dashed',
                        },
                    },
                ],
            } : { data: [] },
        }

        return { newHeadingSpeedSeries, newLinesSeries }
    }

    // Zone bars only depend on transformedBarData/_filteredData, which don't
    // change from heading/speed adjustment alone - only this marker moves.
    // Merge just these two series in place (by id) instead of tearing down
    // and rebuilding every stacked zone bar on every heading/speed tick.
    private updateVesselMarkers() {
        if (!this.chart) return
        const { newHeadingSpeedSeries, newLinesSeries } = this.buildVesselMarkerSeries()
        this.chart.setOption({ series: [newLinesSeries, newHeadingSpeedSeries] })
    }

    private getChartOptions() {
        const { newHeadingSpeedSeries, newLinesSeries } = this.buildVesselMarkerSeries()

        const currentHeading = this.roundToIncrement(
            this.currentDegreeInputValue,
            this._degreeIncrement
        );
        const currentSpeed = this._filteredData.findIndex(
            (d: { degree: number }) => d.degree === currentHeading,
        )

        return {
            animation: false,
            hover: { mode: null },
            grid: {
                containLabel: true,
                left: 0,
                right: 0,
                top: 18,
                bottom: 10,
            },
            xAxis: {
                type: 'category',
                data: this.generateXAxisData(),
                axisLine: {
                    onZero: true,
                    lineStyle: {
                        color: chartColors.grey,
                        width: 1,
                    },
                },
                axisTick: { show: false },

                axisLabel: {
                    color: chartColors.greyDark,
                    formatter: (value: string) => `${value}°`,
                    fontSize: 11,
                    interval: 0,
                },
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: 25,
                axisLine: {
                    onZero: true,
                    lineStyle: {
                        color: chartColors.grey,
                        width: 1,
                    },
                },
                splitLine: {
                    show: false,
                },
                axisTick: { show: false },
                axisLabel: {
                    color: chartColors.greyDark,
                    formatter: (value: number) => `${value} kt`,
                    fontSize: 11,
                },
            },
            tooltip: {
                trigger: 'item',
                backgroundColor: 'none',
                borderWidth: 0,
                padding: 0,
                formatter: (params: any) => {
                    if(params.seriesName === 'new-heading-speed') {
                        return `
                        <div class="custom-tooltip">
                            <div class="tooltip-content">
                                Vessel Speed (new): <strong>${this.speedInputValue.toFixed(2)} kt</strong><br/>
                                Vessel Heading (new): <strong>${this.headingInputValue.toFixed(2)}°</strong>
                            </div>
                            <div class="tooltip-arrow"></div>
                        </div>`
                    }
                    if(params.seriesName === 'current-heading-speed') {
                        return `
                        <div  class="custom-tooltip">
                            <div class="tooltip-content">
                                Vessel Speed (current): <strong>${this.currentSpeedInputValue.toFixed(2)} kt</strong><br/>
                                Vessel Heading (current): <strong>${this.currentDegreeInputValue.toFixed(2)}°</strong>
                            </div>
                            <div class="tooltip-arrow"></div>
                        </div>`
                    }
                    
                },
                position: (
                    point: number[],
                    _: any,
                    __: HTMLElement,
                    ___: any,
                    size: any,
                ) => {
                    const [x, y] = point
                    const { contentSize } = size
                    return [x + 20, y - contentSize[1] / 2]
                },
            },

            series: [
                ...this.generateSeriesData(),
                {
                    name: 'Cross Lines',
                    type: 'line',
                    silent: true,
                    markLine: {
                        symbol: 'none',
                        label: { show: false },
                        data: [
                            {
                                yAxis: this.currentSpeedInputValue,
                                lineStyle: {
                                    color: chartColors.black,
                                    width: 1,
                                    type: 'dashed',
                                },
                            },
                            {
                                xAxis: currentSpeed, // Position at x = 45 across y-axis range
                                lineStyle: {
                                    color: chartColors.black,
                                    width: 1,
                                    type: 'dashed',
                                },
                            },
                        ],
                    },
                },
                newLinesSeries,
                newHeadingSpeedSeries,
                {
                    name: 'current-heading-speed',
                    type: 'custom',
                    data: [[currentSpeed, this.currentDegreeInputValue]],
                    triggerLineEvent: true,
                    renderItem: (
                        _: any,
                        api: {
                            coord: (arg0: any[]) => any[]
                            value: (arg0: number) => any
                        },
                    ) => {
                        const fixedX = api.coord([currentSpeed, this.currentSpeedInputValue])[0]; 
                        const fixedY = api.coord([currentSpeed, this.currentSpeedInputValue])[1];
                        return {
                            type: 'image',
                            style: {
                                height: 18,
                                width: 18,
                                x: fixedX - 18 / 2,
                                y: fixedY - 18 / 2,
                            },
                            emphasis: {
                                disabled: true,
                            },
                        }
                    },
                    z: 100,
                },
            ],
            graphic: this.getCustomGraphics(),
        }
    }

    private generateXAxisData() {
        const steps = []
        const min = this._filteredData[0].degree
        const max = this._filteredData[this._chartRangeLength - 1].degree
        for (let i = min; i <= max; i += this._degreeIncrement) {
            steps.push(i)
        }

        return steps
    }

    private generateSeriesData() {
        const series: {
            name: string
            type: string
            stack: string
            data: any[]
            itemStyle: { color: string }
            silent: boolean
            barWidth: string
            barGap: string
        }[] = []

        this._filteredData.forEach(
            (
                dataPoint: {
                    zones: { type: ZoneTypes; range: number[] }[]
                    degree: any
                },
                idx: number,
            ) => {
                dataPoint.zones.forEach(
                    (zone: { type: ZoneTypes; range: number[] }) => {
                        series.push({
                            name: `${zone.type} (${dataPoint.degree}°)`,
                            type: 'bar',
                            stack: 'total',
                            data: this.getBarDataForRange(zone.range, idx),
                            itemStyle: {
                                color: this.getZoneColor(zone.type),
                            },
                            silent: true,
                            barWidth: '94%',
                            barGap: '6%',
                        })
                    },
                )
            },
        )

        return series
    }

    getZoneColor(zone: ZoneTypes) {
        const colors = {
            safeZone: chartColors.safeZone, // green
            heavyRollingZone: chartColors.heavyRollingZone, // purple
            parametricRollZone: chartColors.parametricRollZone, // pink
            transverseAccelerationZone: chartColors.transverseAccelerationZone, // brown
            constraintZone: chartColors.constraintZone, // blue
        }
        return colors[zone] || chartColors.constraintZone
    }

    getCustomGraphics() {
        if (!this.polarPlotData?.weather) return []

        const arrowWidth = 10
        const iconSize = 18

        const { wind_direction, wave_direction, swell_direction } =
            this.polarPlotData?.weather || {}

        const arrows = [
            { value: wind_direction, color: chartColors.waveDirection },
            { value: wave_direction, color: chartColors.windDirection },
            { value: swell_direction, color: chartColors.swellDirection },
        ]

        const currentIconSVG =
            'data:image/svg+xml,' +
            encodeURIComponent(SvgIcons.customChartCircle.strings[0])

        const finalPositions = arrows
            .map((arrow) => {
                const position = this.calculatePosition(arrow.value)

                if (position === null) return null
                return {
                    type: 'image',
                    z: 100,
                    left: position - arrowWidth / 2,
                    top: 0,
                    style: {
                        image: getArrowSVG(arrow.color), // Replace with the actual arrow image path
                        width: arrowWidth,
                        height: 18,
                        color: arrow.color,
                    },
                    emphasis: {
                        disabled: true,
                    },
                }
            })
            .filter((arrow) => arrow !== null)

            if (this.currentDegreeInputValue !== undefined && this.currentDegreeInputValue !== null) {
                const currentPosition = this.calculatePosition(this.currentDegreeInputValue);
                const topPosition = -(this.currentSpeedInputValue * 6.15) + 162; //6.15, 162 in pixels, manually calculated due to echarts restrictions

                if (currentPosition !== null) {
                    finalPositions.push({
                        type: 'image',
                        z: 100,
                        left: currentPosition - 20 / 2,
                        top:  topPosition,
                        style: {
                            image: currentIconSVG, 
                            width: iconSize,
                            height: iconSize,
                            color: "" 
                        },
                        emphasis: {
                            disabled: true,
                        },
                    });
                }
            }

        return finalPositions
    }

    // returns pixel value
    calculatePosition(degree: number): number | null {
        const chartWidth = this.chart.getWidth()
        const yAxisWidth = 33 // in pixels, manually calculated - this value should be dynamic but echarts does not support it :(
        const xAxisValues = this.generateXAxisData()
        const totalSteps = xAxisValues.length

        const individualBarWidth = (chartWidth - yAxisWidth) / totalSteps

        // since the chart is in X degree increments, I am rounding the degree to the nearest X degree increment
        const roundedDegree = this.roundToIncrement(degree, this._degreeIncrement);

        const baseIndex = xAxisValues.indexOf(roundedDegree)

        const modulo = degree % this._degreeIncrement
        const degreeAdjustmentFactor = modulo / 100

        const adjustedIndex = baseIndex + degreeAdjustmentFactor

        // adding half of the individual bar width to get the center of the bar
        return adjustedIndex < 0 || adjustedIndex > totalSteps - 1
            ? null
            : individualBarWidth * adjustedIndex +
                  yAxisWidth +
                  individualBarWidth / 2
    }

    private handleValueChange(event: CustomEvent, type: 'heading' | 'speed') {
        const eventName = type === 'heading' ? 'new-heading' : 'new-speed'
        this.dispatchEvent(
            new CustomEvent(eventName, {
                detail: event.detail,
                bubbles: true,
                composed: true,
            }),
        )
    }

    render() {
        return html`
            <div class="adjustment-buttons-wrapper">
                <number-stepper
                    .value=${Math.round(this.headingInputValue)}
                    .resetValue=${Math.round(this.polarPlotData?.course_over_ground ?? 0)}
                    .minValue=${0}
                    .maxValue=${360}
                    .isContinious=${true}
                    .label=${'Adjust heading (degree)'}
                    textAlign="left"
                    @value-change=${(e: CustomEvent) =>
                        this.handleValueChange(e, 'heading')}
                ></number-stepper>
                <number-stepper
                    .value=${Math.round(this.speedInputValue)}
                    .resetValue=${Math.round(this.polarPlotData?.speed_over_ground ?? 0)}
                    @value-change=${(e: CustomEvent) =>
                        this.handleValueChange(e, 'speed')}
                    .minValue=${0}
                    .maxValue=${25}
                    .label=${'Adjust Speed (kt)'}
                    textAlign="right"
                ></number-stepper>
            </div>

            <div id="chart" style="width: 100%; height: 200px"></div>
        `
    }
}
